import React from "react";
import {
  Badge,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaDownload } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import SkillChipGroup from "./SkillChipGroup";
import { formatCost, formatAvailability } from "./submissionFormatters";
import {
  PARTNERS_SUBMISSION_STATUS_LABELS,
  PARTNERS_SUBMISSION_STATUS_COLORS,
} from "../../../constants/statuses";
import { StatusBadge } from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

/**
 * Detail view of a single partner candidate submission.
 *
 * Shows only the non-personal, extracted profile fields. Personal info
 * (name, email, phone) is intentionally never rendered even though it may
 * be present in the submission record.
 *
 * Props:
 *   - submission: the submission object (falsy = modal closed)
 *   - onClose:    called when the user dismisses the modal
 *   - showSubmitter: whether to render the "Submitted By" field (owner-side only)
 *   - onDownloadCv: optional (submissionId) => void — renders download button when provided
 *   - downloadingCvKey: id currently downloading, for the loading state
 */
const SubmissionDetailModal = ({
  submission,
  onClose,
  showSubmitter = false,
  onDownloadCv,
  downloadingCvKey,
}) => {
  const { t } = useTranslation("portal");
  if (!submission) return null;

  const title = submission.title
    || t("partners.missions.detail.submissionFallback", "Submission #{{id}}", { id: submission.id });
  const hasCv = Boolean(submission.sourceCvUrl || submission.cvUrl);

  return (
    <Modal isOpen={!!submission} onClose={onClose} size="lg">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <SimpleGrid columns={2} spacing={3}>
              {showSubmitter && (
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    {t("partners.missions.detail.submitter", "Submitted By")}
                  </Text>
                  <Text fontSize="sm">{submission.submitterCompanyName || "-"}</Text>
                </Box>
              )}
              <Box>
                <Text fontSize="xs" color="gray.500">{t("partners.missions.detail.location", "Location")}</Text>
                <Text fontSize="sm">{submission.location || "-"}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">{t("partners.missions.detail.score", "Score")}</Text>
                {submission.score != null ? (
                  <Badge colorScheme={submission.score >= 80 ? "green" : submission.score >= 50 ? "yellow" : "red"}>
                    {submission.score}%
                  </Badge>
                ) : <Text fontSize="sm">-</Text>}
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">{t("partners.missions.detail.cost", "Cost")}</Text>
                <Text fontSize="sm">{formatCost(submission.costAmount, submission.costType)}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">{t("partners.missions.detail.availability", "Availability")}</Text>
                <Text fontSize="sm">
                  {formatAvailability(submission.availabilityDate, submission.availabilityStatus)}
                </Text>
              </Box>
              {submission.totalYearsExperience != null && (
                <Box>
                  <Text fontSize="xs" color="gray.500">{t("partners.missions.detail.experience", "Experience")}</Text>
                  <Text fontSize="sm">
                    {submission.totalYearsExperience} {t("partners.missions.detail.years", "years")}
                  </Text>
                </Box>
              )}
              <Box>
                <Text fontSize="xs" color="gray.500">{t("common.status", "Status")}</Text>
                <StatusBadge
                  label={PARTNERS_SUBMISSION_STATUS_LABELS[submission.status] || submission.status}
                  colorScheme={PARTNERS_SUBMISSION_STATUS_COLORS[submission.status] || "gray"}
                />
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">{t("common.createdAt", "Date")}</Text>
                <Text fontSize="sm">
                  {submission.createdAt ? new Date(submission.createdAt).toLocaleDateString() : "-"}
                </Text>
              </Box>
            </SimpleGrid>

            {submission.description && (
              <Box>
                <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
                  {t("partners.missions.detail.profile", "Profile")}
                </Text>
                <Text fontSize="sm" whiteSpace="pre-wrap">{submission.description}</Text>
              </Box>
            )}

            <SkillChipGroup
              category="technical"
              label={t("partners.missions.detail.technicalSkills", "Technical Skills")}
              skills={submission.skillsTechnical}
            />
            <SkillChipGroup
              category="functional"
              label={t("partners.missions.detail.functionalSkills", "Functional Skills")}
              skills={submission.skillsFunctional}
            />
            <SkillChipGroup
              category="business"
              label={t("partners.missions.detail.businessSkills", "Business Skills")}
              skills={submission.skillsBusiness}
            />

            {hasCv && onDownloadCv && (
              <Button
                size="sm"
                leftIcon={<FaDownload />}
                variant="secondary"
                onClick={() => onDownloadCv(submission.id)}
                isLoading={downloadingCvKey === `submission-${submission.id}`}
                loadingText={t("partners.missions.detail.downloadingCv", "Downloading...")}
                alignSelf="flex-start"
              >
                {t("partners.missions.detail.downloadCv", "Download CV")}
              </Button>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t("partners.missions.detail.close", "Close")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SubmissionDetailModal;
